// src/hooks/useExpandInPlace.js
//
// Shared open/close identity for "expand in place" UI — a row or card that expands inline
// (in document flow, reflowing what follows it) rather than navigating away or overlaying.
// Extracted from the Directory accordion's controlled/uncontrolled toggle idiom, which was
// duplicated six times across DirectoryNav (category level + listing level), ContactList, and
// UtilityCategoryList (category level + listing level) — one source of truth for "am I open,
// and who owns that state" instead of six copies that could drift apart.
//
// Controlled vs uncontrolled: pass { openId, onToggle } to lift the open id to a parent, so
// several sibling groups can share one open-at-a-time identity (the way DirectoryColumn shares
// openListingId across all three directory sections); omit both to hold the open id locally
// (the way SidebarLocality's popout drill does today, with each section independent).
//
// In controlled mode, `toggle` forwards the raw id straight to `onToggle` — the PARENT decides
// open vs close by comparing against its own current value, mirroring DirectoryColumn's existing
// handleToggleListing/handleToggleCategory/handleToggleContact. In uncontrolled mode, `toggle`
// does that open/close comparison itself, since there's no parent to do it. Passing `null` always
// force-closes in both modes. The returned `controlled` flag lets a consumer that composes two
// groups (e.g. a category group that must also force-close a sibling card group) know whether
// that clearing is its own job or already handled one level up — see DirectoryColumn.
//
// What this deliberately does NOT own: reflow markup (each consumer renders its own expanded
// content in-flow — this hook has no opinion on layout, so it can never introduce an overlay by
// accident) and scroll-into-view-on-open. ConcertinaThread.jsx's openThread (chat) does the
// latter today with its own ref registry and an explicit scrollIntoView call; a future consumer
// of this hook that needs the same guarantee can watch the returned openId in its own effect and
// do the same. Not built into this hook until a real consumer needs it — Directory's own current
// behaviour doesn't do this either, and this extraction changes no behaviour.
//
// defaultOpenId (Directory column shell brief, Scope 3): the uncontrolled-mode starting value —
// used by the new section-level group (Directory open on load) alongside content-level groups
// that still want the original null start. Ignored in controlled mode, where the initial value is
// whatever the parent's own state already holds. Every existing caller omits this and keeps
// starting from null, unchanged.

import { useCallback, useState } from 'react';

export function useExpandInPlace({ openId: controlledOpenId, onToggle, defaultOpenId = null } = {}) {
  const [localOpenId, setLocalOpenId] = useState(defaultOpenId);
  const controlled = typeof onToggle === 'function';
  const openId = controlled ? controlledOpenId : localOpenId;

  const toggle = useCallback((id) => {
    if (controlled) {
      onToggle(id);
    } else {
      setLocalOpenId(prev => (prev === id ? null : id));
    }
  }, [controlled, onToggle]);

  const isOpen = useCallback((id) => openId === id, [openId]);

  return { openId, isOpen, toggle, controlled };
}
